import React, { useState, useEffect, useContext } from "react";
import { Context } from "../../context/Context";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { format } from "date-fns";
import { pl } from "date-fns/locale"; // Importuj lokalizację polską
import DatePicker from "react-datepicker"; // Importuj DatePicker
import "react-datepicker/dist/react-datepicker.css"; // Importuj style

const UserBooking = () => {
  const { token } = useContext(Context);
  const [karnety, setKarnety] = useState([]);
  const [selectedKarnet, setSelectedKarnet] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // Ustaw na null
  const [selectedTime, setSelectedTime] = useState("");
  const [availableHours, setAvailableHours] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [loadingHours, setLoadingHours] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const trainingDurations = {
    "Solo EMS": 30,
    "Para EMS": 30,
    "Solo Personalny": 60,
    "Para Personalny": 60,
    Hybryda: 60,
  };

  // Fetch user's karnety on component mount
  useEffect(() => {
    const fetchKarnety = async () => {
      if (!token) {
        setError("No token found. Please log in.");
        setLoading(false);
        return;
      }

      try {
        const decodedToken = jwtDecode(token);
        const userId = decodedToken._id;

        const response = await axios.get(
          `${BACKEND_URL}/api/karnet/getKarnetByUserId/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          const activeKarnety = response.data.filter(
            (karnet) => karnet.aktywny && karnet.ilosc_wejsc_kal > 0
          );
          setKarnety(activeKarnety);
        } else {
          setError("No active karnety found.");
        }
      } catch (err) {
        setError(`Error fetching karnety: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchKarnety();
  }, [token]);

  const checkSlotAvailability = async (date, time, typTreningu, trainerId) => {
    const duration = trainingDurations[typTreningu] || 60; // Domyślny czas 60 minut
    const startDateTime = `${format(date, "yyyy-MM-dd")}T${time}`;
    const endDateTime = new Date(
      new Date(startDateTime).getTime() + duration * 60 * 1000
    ).toISOString();

    const slotsToCheck = [];
    const startTime = new Date(startDateTime);
    const endTime = new Date(endDateTime);

    let currentTime = startTime;
    while (currentTime < endTime) {
      slotsToCheck.push(format(currentTime, "HH:mm"));
      currentTime = new Date(currentTime.getTime() + 30 * 60 * 1000); // Dodaj 30 minut
    }

    const availabilityResponse = await axios.get(
      `${BACKEND_URL}/api/treningi/getAvailableHours`,
      {
        params: {
          date: format(date, "yyyy-MM-dd"),
          typTreningu: typTreningu,
        },
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const availableHours = availabilityResponse.data.availableHours || [];
    return slotsToCheck.every((slot) => {
      const timeSlot = availableHours.find((h) => h.time === slot);
      return (
        timeSlot &&
        timeSlot.availableTrainers.some((trainer) => trainer._id === trainerId)
      );
    });
  };
  useEffect(() => {
    const fetchAvailableHours = async () => {
      if (!selectedDate || !selectedKarnet) return;
  
      setLoadingHours(true);
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/treningi/getAvailableHours`,
          {
            params: {
              date: format(selectedDate, "yyyy-MM-dd"),
              typTreningu: selectedKarnet.rodzaj,
              trener_id: selectedTrainer?._id
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        // Używaj danych bezpośrednio z backendu
        setAvailableHours(response.data.availableHours || []);
      } catch (err) {
        console.error("Błąd pobierania godzin:", err);
      } finally {
        setLoadingHours(false);
      }
    };
  
    fetchAvailableHours();
  }, [selectedDate, selectedKarnet, token]);

  const handleTimeSelection = (time) => {
    setSelectedTime(time);
    const timeSlot = availableHours.find((h) => h.time === time);
    if (timeSlot && timeSlot.availableTrainers.length > 0) {
      setSelectedTrainer(timeSlot.availableTrainers[0]);
    } else {
      setSelectedTrainer(null);
    }
  };

  const handleBooking = async () => {
    if (!selectedKarnet || !selectedDate || !selectedTime || !selectedTrainer) {
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const userId = decodedToken._id;

      const startDateTime = `${format(
        selectedDate,
        "yyyy-MM-dd"
      )}T${selectedTime}`;

      // Ustal czas trwania na podstawie typu treningu
      const duration = trainingDurations[selectedKarnet.rodzaj] || 60; // Domyślny czas 60 minut
      const endDateTime = new Date(
        new Date(startDateTime).getTime() + duration * 60 * 1000 // Dodaj czas trwania w milisekundach
      ).toISOString();

      // Jeśli wszystkie sloty są dostępne, kontynuuj rezerwację
      await axios.post(
        `${BACKEND_URL}/api/treningi/addTreningi`,
        {
          trener_id: selectedTrainer._id,
          uzytkownicy: userId,
          karnet_id: selectedKarnet._id,
          data: new Date(startDateTime),
          godzina_start: new Date(startDateTime),
          godzina_koniec: new Date(endDateTime),
          typ_treningu: selectedKarnet.rodzaj,
          status: "active",
          bookedBy: "user",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Aktualizuj karnet
      await axios.put(
        `${BACKEND_URL}/api/karnet/updateKarnet/${selectedKarnet._id}`,
        {
          ilosc_wejsc_kal: selectedKarnet.ilosc_wejsc_kal - 1, // Zmniejsz liczbę wejść
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setBookingSuccess(true);

      // Resetuj formularz
      setSelectedDate(null);
      setSelectedTime("");
      setSelectedTrainer(null);
      setSelectedKarnet(null);

      // Odśwież listę karnetów
      const updatedKarnetyResponse = await axios.get(
        `${BACKEND_URL}/api/karnet/getKarnetByUserId/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setKarnety(
        updatedKarnetyResponse.data.filter(
          (karnet) => karnet.aktywny && karnet.ilosc_wejsc > 0
        )
      );
    } catch (err) {
      console.error("Error booking training:", err);
      setError("Wystąpił błąd podczas rezerwacji. Spróbuj ponownie.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div
      className="max-w-lg mx-auto mt-8 p-10 bg-white rounded-lg shadow-md"
      style={{ maxHeight: "90vh", position: "relative" }}
    >
      <h2 className="text-2xl font-bold mb-6 text-center">
        Zarezerwuj Trening
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {bookingSuccess && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
          Trening został pomyślnie zarezerwowany!
        </div>
      )}

      {/* Karnet Selection */}
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Wybierz Karnet *
        </label>
        <select
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={selectedKarnet?._id || ""}
          onChange={(e) => {
            const karnet = karnety.find((k) => k._id === e.target.value);
            setSelectedKarnet(karnet);
            setSelectedTime("");
            setSelectedTrainer(null);
          }}
        >
          <option value="">Wybierz karnet</option>
          {karnety.map((karnet) => (
            <option key={karnet._id} value={karnet._id}>
              {karnet.nazwa} - {karnet.rodzaj} (pozostało wejść:{" "}
              {karnet.ilosc_wejsc_kal})
            </option>
          ))}
        </select>
      </div>

      {/* Date Selection */}
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2">
          Wybierz Datę *
        </label>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setSelectedTime("");
            setSelectedTrainer(null);
          }}
          minDate={new Date()} // Ustaw minimalną datę na dzisiaj
          filterDate={(date) => date.getDay() !== 0} // Blokuj weekendy
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholderText="Wybierz datę"
          locale={pl} // Ustaw lokalizację na polski
        />
      </div>

      {/* Time Selection */}
      {selectedDate && selectedKarnet && (
        <div className="mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Wybierz Godzinę *
          </label>
          {loadingHours ? (
            <p>Ładowanie dostępnych godzin...</p>
          ) : availableHours.length === 0 ? (
            <p className="text-red-500">Brak dostępnych godzin w wybranym dniu.</p>
          ) : (
            <select
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedTime}
              onChange={(e) => handleTimeSelection(e.target.value)}
            >
              <option value="">Wybierz godzinę</option>
              {availableHours?.map(({ time, availableTrainers }) => (
                <option
                  key={time}
                  value={time}
                  disabled={availableTrainers.length === 0}
                >
                  {time} {availableTrainers.length === 0 ? "(brak miejsc)" : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Booking Summary */}
      {selectedKarnet && selectedDate && selectedTime && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-bold text-blue-800 mb-2">
            Podsumowanie Rezerwacji
          </h3>
          <p className="text-sm text-blue-600">
            Rodzaj Treningu: {selectedKarnet.rodzaj}
            <br />
            Data: {format(selectedDate, "d MMMM yyyy")}
            <br />
            Godzina: {selectedTime}
          </p>
        </div>
      )}

      {/* Book Button */}
      <button
        className={`w-full p-3 rounded-lg text-white font-bold transition-colors
          ${
            !selectedKarnet ||
            !selectedDate ||
            !selectedTime ||
            !selectedTrainer
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-blue-500 hover:bg-blue-600"
          }`}
        onClick={handleBooking}
        disabled={
          !selectedKarnet || !selectedDate || !selectedTime || !selectedTrainer
        }
      >
        Zarezerwuj Trening
      </button>

      {/* Required Fields Note */}
      <p className="mt-4 text-sm text-gray-500 text-center">* Pola wymagane</p>
    </div>
  );
};

export default UserBooking;
